import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { AdminUser, CreateUserData } from "@/lib/admin-types";
import { authClient } from "@/lib/auth-client";

const MIN_PASSWORD_LENGTH = 8;
const APP_USER_ROLES = ["user", "sudo", "consumer"] as const;

type AppUserRole = (typeof APP_USER_ROLES)[number];

function toAppUserRole(role: string | null | undefined): AppUserRole {
  return role === "sudo" || role === "consumer" ? role : "user";
}

const userFormSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  name: z.string().min(1, "Name is required"),
  password: z
    .string()
    .min(MIN_PASSWORD_LENGTH, "Password must be at least 8 characters")
    .optional(),
  role: z.enum(APP_USER_ROLES),
  emailVerified: z.boolean(),
});

type UserFormData = z.infer<typeof userFormSchema>;

type UserFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: AdminUser | null;
  onSuccess: () => void;
};

export function UserForm({ open, onOpenChange, user, onSuccess }: UserFormProps) {
  const { t } = useTranslation();
  const isEditing = !!user;

  const form = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: user?.email || "",
      name: user?.name || user?.email?.split("@")[0] || "",
      password: "",
      role: toAppUserRole(user?.role),
      emailVerified: user?.emailVerified,
    },
  });

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      form.reset({
        email: user.email,
        name: user.name || "",
        password: "",
        role: toAppUserRole(user.role),
        emailVerified: user.emailVerified,
      });
    } else {
      form.reset({
        email: "",
        name: "",
        password: "",
        role: "user",
        emailVerified: false,
      });
    }
  }, [user, form]);

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: CreateUserData) => {
      // Better Auth's generated client type only exposes its default roles,
      // while the server registers the lncd-specific sudo and consumer roles.
      const response = await authClient.admin.createUser(
        data as unknown as Parameters<typeof authClient.admin.createUser>[0],
      );
      if (!response.data) {
        throw new Error("Failed to create user");
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("admin.user.form.createSuccess"));
      onSuccess();
      onOpenChange(false);
      form.reset();
    },
    onError: (error) => {
      toast.error(t("admin.user.form.createFailed", { error: error.message }));
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async (data: Partial<AdminUser>) => {
      if (!user) {
        throw new Error("No user to update");
      }

      const response = await authClient.admin.updateUser({
        userId: user.id,
        data,
      });
      if (!response.data) {
        throw new Error("Failed to update user");
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("admin.user.form.updateSuccess"));
      onSuccess();
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(t("admin.user.form.updateFailed", { error: error.message }));
    },
  });

  const handleUserUpdate = (data: UserFormData) => {
    if (!user) {
      return;
    }
    const updates: Partial<AdminUser> = {};
    if (data.email !== user.email) {
      updates.email = data.email;
    }
    if (data.name !== user.name) {
      updates.name = data.name;
    }
    if (data.role !== user.role) {
      updates.role = data.role;
    }
    if (data.emailVerified !== user.emailVerified) {
      updates.emailVerified = data.emailVerified;
    }
    updateUserMutation.mutate(updates);
  };

  const handleUserCreation = (data: UserFormData) => {
    if (!data.password) {
      toast.error(t("admin.user.form.passwordRequired"));
      return;
    }

    const createData = {
      email: data.email,
      password: data.password,
      name: data.name || data.email.split("@")[0],
      role: data.role as "user" | "sudo" | "consumer",
    };

    createUserMutation.mutate(createData);
  };

  const onSubmit = (data: UserFormData) => {
    if (isEditing) {
      handleUserUpdate(data);
    } else {
      handleUserCreation(data);
    }
  };

  const isLoading = createUserMutation.isPending || updateUserMutation.isPending;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("admin.user.form.editTitle") : t("admin.user.form.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t("admin.user.form.editDescription")
              : t("admin.user.form.createDescription")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("admin.user.form.email")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("admin.user.form.emailPlaceholder")}
                      {...field}
                      disabled={isEditing}
                      autoComplete="email"
                      type="email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("admin.user.form.name")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("admin.user.form.namePlaceholder")}
                      {...field}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isEditing && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("admin.user.form.password")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("admin.user.form.passwordPlaceholder")}
                        autoComplete="new-password"
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("admin.user.form.role")}</FormLabel>
                  <Select defaultValue={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("admin.user.form.selectRole")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="user">{t("admin.user.form.user")}</SelectItem>
                      <SelectItem value="sudo">{t("admin.user.form.admin")}</SelectItem>
                      <SelectItem value="consumer">
                        {t("admin.user.form.consumer", "Consumer")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="emailVerified"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel>{t("admin.user.form.emailVerifiedLabel")}</FormLabel>
                    <FormDescription>{t("admin.user.form.emailVerifiedDesc")}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                disabled={isLoading}
                onClick={() => onOpenChange(false)}
                type="button"
                variant="outline"
              >
                {t("admin.user.form.cancel")}
              </Button>
              <Button disabled={isLoading} type="submit">
                {(() => {
                  if (isLoading) {
                    return isEditing
                      ? t("admin.user.form.updating")
                      : t("admin.user.form.creating");
                  }
                  return isEditing ? t("admin.user.form.update") : t("admin.user.form.create");
                })()}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
